/**
 * "Search by photo" — the camera button inside the shop's search bar opens this panel.
 * Server: POST /api/shop/image-search (worker/src/image-search.ts). The photo is shrunk in the
 * browser, posted once, and never kept — the panel says so. Results are ordinary product cards
 * (the same projection as every grid); "Not it? Search by name" carries the AI's words over.
 */
import { useEffect, useId, useRef, useState } from "react";
import type { ShopCard } from "./types";
import type { GoFn } from "./route";
import { Drawer } from "./ui";
import { ProductCard } from "./Listing";
import { PHOTO_NOTE, postPhoto, shrinkForSearch } from "../photoSearch";

type State =
  | { k: "idle" }
  | { k: "busy"; preview: string }
  | { k: "done"; preview: string; products: ShopCard[]; keywords: string[] }
  | { k: "error"; preview: string | null; message: string; keywords: string[] };

export function PhotoSearch({ open, onClose, go, trade }: { open: boolean; onClose: () => void; go: GoFn; trade: boolean }) {
  const uid = useId();
  const [state, setState] = useState<State>({ k: "idle" });
  const camera = useRef<HTMLInputElement | null>(null);
  const gallery = useRef<HTMLInputElement | null>(null);
  const abort = useRef<AbortController | null>(null);
  const previewUrl = useRef<string | null>(null);

  const dropPreview = () => {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
  };
  useEffect(() => () => { abort.current?.abort(); dropPreview(); }, []);
  useEffect(() => {
    if (!open) { abort.current?.abort(); dropPreview(); setState({ k: "idle" }); }
  }, [open]);

  const run = async (file: File | undefined) => {
    if (!file) return;
    abort.current?.abort();
    dropPreview();
    const blob = await shrinkForSearch(file);
    if (!blob) {
      setState({ k: "error", preview: null, message: "We could not read that photo. Try another one, or take a new photo.", keywords: [] });
      return;
    }
    const preview = URL.createObjectURL(blob);
    previewUrl.current = preview;
    setState({ k: "busy", preview });
    const ac = new AbortController();
    abort.current = ac;
    try {
      const r = await postPhoto<{ products: ShopCard[]; keywords: string[] }>("/shop/image-search", blob, ac.signal);
      if (ac.signal.aborted) return;
      if (r.ok) setState({ k: "done", preview, products: r.products, keywords: r.keywords ?? [] });
      else setState({ k: "error", preview, message: r.message, keywords: [] });
    } catch {
      /* aborted: the panel closed or a new photo was chosen */
    }
  };

  const byName = (keywords: string[]) => {
    const q = keywords.slice(0, 2).join(" ").trim();
    onClose();
    go(q ? { name: "search", qs: new URLSearchParams({ q }).toString() } : { name: "search" });
  };

  const pickers = (
    <div className="ps-pick">
      <button type="button" className="btn" onClick={() => camera.current?.click()}>Take a photo</button>
      <button type="button" className="btn btn-quiet" onClick={() => gallery.current?.click()}>Choose a photo</button>
      {/* Two inputs: `capture` opens the camera directly on a phone; the other offers the gallery and files. */}
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void run(e.target.files?.[0]); e.target.value = ""; }} aria-hidden="true" tabIndex={-1} />
      <input ref={gallery} type="file" accept="image/*" hidden onChange={(e) => { void run(e.target.files?.[0]); e.target.value = ""; }} aria-hidden="true" tabIndex={-1} />
    </div>
  );

  return (
    <Drawer open={open} onClose={onClose} side="right" wide title="Search by photo">
      <div className="ps" aria-describedby={`${uid}-note`}>
        {state.k === "idle" && <p>Have a photo of what you want? Take one or choose one, and we will find products that look like it.</p>}
        {state.k !== "idle" && state.preview && <img className="ps-preview" src={state.preview} alt="Your photo" />}
        <div aria-live="polite" role="status">
          {state.k === "busy" && <p className="ps-busy">Looking for matches…</p>}
          {state.k === "done" && <h3 className="ps-h">{state.products.length ? "Showing products that look like your photo" : "We could not find a match for that photo"}</h3>}
          {state.k === "error" && <p className="ps-err">{state.message}</p>}
        </div>
        {state.k === "done" && state.products.length > 0 && (
          <div className="grid ps-grid" onClickCapture={onClose}>
            {state.products.map((p, i) => <ProductCard key={p.handle} p={p} i={i} go={go} trade={trade} />)}
          </div>
        )}
        {(state.k === "done" || state.k === "error") && (
          <p className="ps-fallback">
            Not it? <button type="button" onClick={() => byName(state.keywords)}>
              Search by name{state.keywords.length ? ` for “${state.keywords.slice(0, 2).join(" ")}”` : ""}
            </button>
          </p>
        )}
        {state.k !== "busy" && pickers}
        <p className="ps-note" id={`${uid}-note`}>{PHOTO_NOTE}</p>
      </div>
    </Drawer>
  );
}
